import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Person, Save, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { usersApi } from '../services/api';
import { UserUpdate } from '../types';
import { useAuth } from '../context/AuthContext';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const updateMutation = useMutation(usersApi.updateMe, {
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setSuccess('Profile updated successfully');
      setError('');
    },
    onError: (error: any) => {
      setError(error.response?.data?.detail || 'Failed to update profile');
      setSuccess('');
    }
  });

  const passwordMutation = useMutation(usersApi.changePassword, {
    onSuccess: () => {
      setPasswordSuccess('Password changed successfully');
      setPasswordError('');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    },
    onError: (error: any) => {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
      setPasswordSuccess('');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updateData: UserUpdate = {};

    // Only include fields that have changed
    if (formData.first_name !== user?.first_name) {
      updateData.first_name = formData.first_name;
    }
    if (formData.last_name !== user?.last_name) {
      updateData.last_name = formData.last_name;
    }
    if (formData.phone !== user?.phone) {
      updateData.phone = formData.phone;
    }

    // Only submit if there are changes
    if (Object.keys(updateData).length > 0) {
      updateMutation.mutate(updateData);
    } else {
      setError('No changes to save');
    }
  };

  const hasChanges = () => {
    return (
      formData.first_name !== user?.first_name ||
      formData.last_name !== user?.last_name ||
      formData.phone !== user?.phone
    );
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate passwords
    if (passwordData.new_password.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    passwordMutation.mutate({
      current_password: passwordData.current_password,
      new_password: passwordData.new_password,
    });
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          My Profile
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Account Information Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Person sx={{ mr: 1 }} />
                  <Typography variant="h6">Account Information</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {user?.email}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Username
                  </Typography>
                  <Typography variant="body1">
                    {user?.username}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Role
                  </Typography>
                  <Chip
                    label={user?.role}
                    color={user?.role === 'manager' ? 'primary' : 'default'}
                    size="small"
                  />
                </Box>

                {user?.employee_id && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Employee ID
                    </Typography>
                    <Typography variant="body1">
                      {user.employee_id}
                    </Typography>
                  </Box>
                )}

                {user?.department && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Department
                    </Typography>
                    <Typography variant="body1">
                      {user.department}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Account Created
                  </Typography>
                  <Typography variant="body1">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York' }) : 'Unknown'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Last Login
                  </Typography>
                  <Typography variant="body1">
                    {user?.last_login ? new Date(user.last_login).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short', hour12: true, timeZone: 'America/New_York' }) : 'Never'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Editable Information Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Personal Information
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  You can update the following information:
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    margin="normal"
                    required
                  />

                  <TextField
                    fullWidth
                    label="Last Name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    margin="normal"
                    required
                  />

                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    margin="normal"
                    helperText="Optional contact number"
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={updateMutation.isLoading || !hasChanges()}
                    fullWidth
                    sx={{ mt: 3 }}
                  >
                    {updateMutation.isLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      'Save Changes'
                    )}
                  </Button>

                  {hasChanges() && (
                    <Typography variant="body2" color="primary" sx={{ mt: 1, textAlign: 'center' }}>
                      You have unsaved changes
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Change Password Card */}
          <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Lock sx={{ mr: 1 }} />
                <Typography variant="h6">Change Password</Typography>
              </Box>

              {passwordError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordError('')}>
                  {passwordError}
                </Alert>
              )}

              {passwordSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPasswordSuccess('')}>
                  {passwordSuccess}
                </Alert>
              )}

              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Update your password to keep your account secure.
              </Typography>

              <Box component="form" onSubmit={handlePasswordChange}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          edge="end"
                        >
                          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  margin="normal"
                  required
                  helperText="Must be at least 8 characters long"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Lock />}
                  disabled={passwordMutation.isLoading}
                  fullWidth
                  sx={{ mt: 3 }}
                >
                  {passwordMutation.isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>
          </Grid>

          {/* Additional Information */}
          <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact Administrator
              </Typography>
              <Typography variant="body2" color="textSecondary">
                If you need to update your email, username, role, employee ID, or department,
                please contact your system administrator. These changes require administrative approval.
              </Typography>
            </CardContent>
          </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Profile;